import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiService } from '../../services/ApiService';

const DebugAuth: React.FC = () => {
  const { user, isAuthenticated, hasRole } = useAuth();
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchDebugInfo = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setDebugInfo({ error: 'No token found' });
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await apiService.get('auth/user', { isPublic: false });

      setDebugInfo({
        status: 200, // ApiService handles the status internally
        data: response,
        token: token.substring(0, 20) + '...',
        hasRole: hasRole('admin'),
        isAuthenticated: isAuthenticated(),
        apiUrl: apiUrl,
      });
    } catch (error: any) {
      setDebugInfo({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebugInfo();
  }, []);

  return (
    <Card className="w-full max-w-2xl mx-auto mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🔍 Auth Debug Panel
          <Button 
            size="sm" 
            variant="outline" 
            onClick={fetchDebugInfo}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold mb-2">Frontend State</h4>
            <div className="space-y-2 text-sm">
              <div>Authenticated: <Badge variant={isAuthenticated() ? "default" : "destructive"}>{isAuthenticated() ? 'Yes' : 'No'}</Badge></div>
              <div>Has Admin Role: <Badge variant={hasRole('admin') ? "default" : "destructive"}>{hasRole('admin') ? 'Yes' : 'No'}</Badge></div>
              <div>User ID: {user?.id || 'None'}</div>
              <div>User Email: {user?.email || 'None'}</div>
              <div>User Role: {user?.role || 'None'}</div>
              <div>User Roles: {user?.roles ? JSON.stringify(user.roles) : 'None'}</div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">Backend Response</h4>
            <div className="space-y-2 text-sm">
              {debugInfo ? (
                <>
                  <div>Status: <Badge variant={debugInfo.status === 200 ? "default" : "destructive"}>{debugInfo.status}</Badge></div>
                  {debugInfo.error ? (
                    <div className="text-red-600">Error: {debugInfo.error}</div>
                  ) : (
                    <>
                      <div>Backend Role: {debugInfo.data?.role || 'None'}</div>
                      <div>Backend Roles: {debugInfo.data?.roles ? JSON.stringify(debugInfo.data.roles) : 'None'}</div>
                    </>
                  )}
                </>
              ) : (
                <div>Loading...</div>
              )}
            </div>
          </div>
        </div>

        {debugInfo && !debugInfo.error && (
          <div>
            <h4 className="font-semibold mb-2">Raw Backend Response</h4>
            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-40">
              {JSON.stringify(debugInfo.data, null, 2)}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DebugAuth; 